import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { clientService } from "../../../../../../api";
import { TopProgress, Typography } from "../../../../../../components";
import { useUi } from "../../../../../../context/ui.context";
import { PageContainer } from "../styles";
import {
  Header,
  Summary,
  SummaryRow,
  SummaryPill,
  ResourcesGrid,
  ResourceCard,
  ResourceHeader,
  CountBadge,
  ResourceList,
  ResourceItem,
  ItemTitle,
  ItemMeta,
  IpList,
  IpTag,
  CardFooter,
  FooterText,
  EmptyCardState,
  SkeletonGrid,
  PageEmptyState,
  ItemLink,
  FooterLink,
} from "./styles";

type CloudResourceItem = {
  id?: string | number;
  name: string;
  fixed_ips?: { subnet_id?: string; ip_address: string }[];
};

type CloudResource = {
  key: string;
  name: string;
  count: number;
  load_count: number;
  objects: CloudResourceItem[];
};

type ResourceRouteConfig = {
  list: string;
  filterField: string;
  build?: (item: CloudResourceItem) => string | undefined;
};

const normalizeKey = (value?: string) => value?.toLowerCase().trim() ?? "";

const buildListUrl = (basePath: string, filterField: string, clientId: string) => {
  return `${basePath}?filtering=${filterField}:${clientId}`;
};

const RESOURCE_ROUTE_MAP: Record<string, ResourceRouteConfig> = {
  instances: {
    list: "/openstack/instances",
    filterField: "project__service__client__id",
    build: (item) => (item.id ? `/openstack/instances/${item.id}` : undefined),
  },
  "database instances": {
    list: "/openstack/database-instances",
    filterField: "project__service__client__id",
  },
  volumes: {
    list: "/openstack/volumes",
    filterField: "project__service__client__id",
    build: (item) => (item.id ? `/openstack/volumes/${item.id}` : undefined),
  },
  "ssh keys": {
    list: "/openstack/ssh-keys",
    filterField: "user__clients__id",
  },
  networks: {
    list: "/openstack/networks",
    filterField: "project__service__client__id",
    build: (item) => (item.id ? `/openstack/networks/${item.id}` : undefined),
  },
  images: {
    list: "/openstack/images",
    filterField: "project__service__client__id",
    build: (item) => (item.id ? `/openstack/images/${item.id}` : undefined),
  },
  routers: {
    list: "/openstack/routers",
    filterField: "project__service__client__id",
    build: (item) => (item.id ? `/openstack/routers/${item.id}` : undefined),
  },
  ports: {
    list: "/openstack/ports",
    filterField: "project__service__client__id",
    build: (item) => (item.id ? `/openstack/ports/${item.id}` : undefined),
  },
};

const CloudResources: FC = () => {
  const [t] = useTranslation();
  const { userId } = useParams();
  const { viewAlert } = useUi();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [resources, setResources] = useState<CloudResource[]>([]);

  const fetchCloudSummary = useCallback(async () => {
    if (!userId) {
      setResources([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await clientService.getCloudSummary(userId);
      const payload = (response?.data || {}) as Record<string, Partial<CloudResource> & { objects?: CloudResourceItem[] }>;

      const normalized: CloudResource[] = Object.entries(payload).map(([key, resource]) => ({
        key,
        name: resource?.name ?? key,
        count: resource?.count ?? 0,
        load_count: resource?.load_count ?? (resource?.objects?.length ?? 0),
        objects: Array.isArray(resource?.objects) ? resource.objects : [],
      }));

      setResources(normalized);
    } catch (error: any) {
      const message = error?.response?.data?.detail || error?.message;
      if (message) {
        viewAlert({
          severity: "error",
          message,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId, viewAlert]);

  useEffect(() => {
    fetchCloudSummary();
  }, [fetchCloudSummary]);

  const totals = useMemo(
    () => ({
      total: resources.reduce((sum, item) => sum + (item.count || 0), 0),
      displayed: resources.reduce((sum, item) => sum + item.objects.length, 0),
    }),
    [resources]
  );

  return (
    <PageContainer>
      {isLoading && <TopProgress />}
      <Header>
        <Summary>
          <Typography variant="h2">{t("CLOUD_RESOURCES")}</Typography>
          <Typography variant="body">
            {t("CLOUD_RESOURCES_SUMMARY", {
              resources: totals.total,
              categories: resources.length,
            })}
          </Typography>
          <SummaryRow>
            <SummaryPill>
              {t("TOTAL_OBJECTS")}: {totals.total}
            </SummaryPill>
            <SummaryPill>
              {t("CLOUD_RESOURCES_DISPLAYED")}: {totals.displayed}
            </SummaryPill>
          </SummaryRow>
        </Summary>
      </Header>

      {isLoading ? (
        <SkeletonGrid>
          {Array.from({ length: 6 }).map((_, index) => (
            <ResourceCard key={index}>
              <Skeleton height={24} width="70%" />
              <Skeleton height={18} width="35%" />
              <Skeleton count={3} height={14} />
            </ResourceCard>
          ))}
        </SkeletonGrid>
      ) : resources.length ? (
        <ResourcesGrid>
          {resources.map((resource) => {
            const normalizedName = normalizeKey(resource.name);
            const normalizedKey = normalizeKey(resource.key);
            const routeConfig =
              RESOURCE_ROUTE_MAP[normalizedName] || RESOURCE_ROUTE_MAP[normalizedKey];

            return (
              <ResourceCard key={resource.key}>
                <ResourceHeader>
                  <Typography variant="h3">{resource.name}</Typography>
                  <CountBadge>{resource.count}</CountBadge>
                </ResourceHeader>

                {resource.objects.length ? (
                  <ResourceList>
                    {resource.objects.map((item) => {
                      const itemHref = routeConfig?.build?.(item);
                      const content = (
                        <>
                          <ItemTitle>{item.name}</ItemTitle>
                          {item.id ? (
                            <ItemMeta>
                              {t("ID")}: {item.id}
                            </ItemMeta>
                          ) : null}
                          {item?.fixed_ips?.length ? (
                            <>
                              <ItemMeta>{t("FIXED_IPS")}</ItemMeta>
                              <IpList>
                                {item.fixed_ips.map((ip) => (
                                  <IpTag key={`${item.id ?? item.name}-${ip.ip_address}`}>
                                    {ip.ip_address}
                                  </IpTag>
                                ))}
                              </IpList>
                            </>
                          ) : null}
                        </>
                      );

                      return (
                        <ResourceItem key={`${resource.key}-${item.id ?? item.name}`}>
                          {itemHref ? <ItemLink to={itemHref}>{content}</ItemLink> : content}
                        </ResourceItem>
                      );
                    })}
                  </ResourceList>
                ) : (
                  <EmptyCardState>
                    <Typography variant="small">{t("CLOUD_RESOURCES_EMPTY")}</Typography>
                  </EmptyCardState>
                )}

                {(resource.count > resource.objects.length || routeConfig?.list) && (
                  <CardFooter>
                    {resource.count > resource.objects.length ? (
                      <FooterText>
                        {t("CLOUD_RESOURCES_SHOWING", {
                          displayed: resource.objects.length,
                          total: resource.count,
                        })}
                      </FooterText>
                    ) : (
                      <span />
                    )}
                    {routeConfig?.list && userId ? (
                      <FooterLink to={buildListUrl(routeConfig.list, routeConfig.filterField, userId)}>
                        {t("CLOUD_RESOURCES_VIEW_ALL")}
                      </FooterLink>
                    ) : null}
                  </CardFooter>
                )}
              </ResourceCard>
            );
          })}
        </ResourcesGrid>
      ) : (
        <PageEmptyState>
          <Typography variant="small">{t("CLOUD_RESOURCES_NO_DATA")}</Typography>
        </PageEmptyState>
      )}
    </PageContainer>
  );
};

export default CloudResources;
